/* @jsx h */
import { defineComponent, h, ref, onMounted } from "vue";
import { BottomSheet, useLocalization } from "@lemmonhq/uikit";
import { TableStatus, Order } from "@lemmonhq/types";
import { TableOrders } from "@app/vue/components/POSComponent/TableOrders";
import { api } from "@app/data/index";
import { RouterLink, useRouter } from "vue-router";
import { Ref } from "vue";
import { useAuth } from "@app/vue/providers/Auth";
import { TableAmountLines } from "@app/vue/components/TableCredit";

export type TableDetailsProps = {
  isOpen: boolean;
  onClose: () => void;
  tableStatus: TableStatus;
  onShowPayment: Function;
  printReceipt: Function;
  showChangeTableModal: Ref<boolean>;
  showTransferItemsModal: Ref<boolean>;
};

export const TableDetails = defineComponent<TableDetailsProps>({
  name: "TableDetails",
  props: [
    "isOpen",
    "onClose",
    "tableStatus",
    "onShowPayment",
    "printReceipt",
    "showChangeTableModal",
    "showTransferItemsModal",
  ],
  setup(props) {
    const auth = useAuth();
    const orders = ref<Order[]>([]);
    const isLoading = ref(true);
    const isPrinting = ref(false);

    const fetchOrders = async () => {
      isLoading.value = true;
      try {
        const orderIds = props.tableStatus.orders.map((order) => order.id);
        if (orderIds.length > 0) {
          const fetchedOrders = await api.getOrders(orderIds);
          orders.value = fetchedOrders;
        } else {
          orders.value = [];
        }
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      fetchOrders();
    });
    const { t } = useLocalization();
    const router = useRouter();

    const renderActionButtons = (isBusy: boolean) => {
      const posLink = `/app/pos?tableId=${props.tableStatus.table.id}`;
      const handleClickOnPOSButton = (e: MouseEvent) => {
        const currentStr = window.location.pathname + window.location.search;
        console.log({ posLink, currentStr });
        if (posLink === currentStr) {
          e.preventDefault();
          e.stopPropagation();
          props.onClose();
        } else {
          router.push(posLink);
        }
      };
      return (
        <div class="grid grid-cols-3 gap-2 p-4 bg-base-200 rounded-lg">
          {isBusy ? (
            <>
              <a class="btn btn-accent" onClick={handleClickOnPOSButton}>
                <span class="flex justify-center items-center gap-2">
                  <span class="material-symbols-outlined">
                    add_shopping_cart
                  </span>
                  <span class="hidden lg:inline">
                    {t("tables.general.add_button")}
                  </span>
                </span>
              </a>
              <RouterLink
                class="btn btn-neutral"
                to={`/app/orders?tableId=${props.tableStatus.table.id}`}
              >
                <span class="flex justify-center items-center gap-2">
                  <span class="material-symbols-outlined">order_approve</span>
                  <span class="hidden lg:inline">
                    {t("tables.general.orders_button")}
                  </span>
                </span>
              </RouterLink>
              <button
                onClick={() => {
                  props.onClose();
                  props.onShowPayment();
                }}
                class="btn btn-primary"
              >
                <span class="flex justify-center items-center gap-2">
                  <span class="material-symbols-outlined">credit_card</span>
                  <span class="hidden lg:inline">
                    {t("tables.general.pay_button")}
                  </span>
                </span>
              </button>
            </>
          ) : (
            <q
              class="btn btn-accent col-span-3"
              onClick={handleClickOnPOSButton}
            >
              {() => (
                <span class="flex justify-center items-center gap-2">
                  <span class="material-symbols-outlined">
                    add_shopping_cart
                  </span>
                  <span class="hidden lg:inline">
                    {t("tables.general.add_orders_button")}
                  </span>
                </span>
              )}
            </q>
          )}
        </div>
      );
    };

    return () => {
      const isBusy = !!props.tableStatus.table.is_busy;

      return (
        <BottomSheet
          title={
            <div class="flex flex-col items-center justify-center w-full">
              <div class="flex items-center gap-1">
                <span class="material-symbols-outlined mr-0 text-1xl">
                  table_restaurant
                </span>
                <span class="text-2xl ">{props.tableStatus.table.name}</span>
                <div
                  class={`badge ${isBusy ? "badge-error" : "badge-success"}`}
                >
                  {isBusy ? t("tables.general.busy") : t("tables.general.free")}
                </div>
              </div>
              {isBusy && (
                <div class="text-xs">
                  <TableAmountLines
                    tableStatus={props.tableStatus}
                    currency={auth.user.value?.restaurant.currency || "CHF"}
                    align="center"
                  />
                </div>
              )}
            </div>
          }
          isOpen={props.isOpen}
          onClose={props.onClose}
          maxHeight="calc(100dvh - 0px)"
        >
          <div style="padding-bottom: 120px;" class="flex flex-col h-full">
            <div class="flex-1 overflow-y-auto p-2 lg:p-4">
              <TableOrders tableId={props.tableStatus.id} />
            </div>
            <div class="absolute left-0 right-0 bottom-0 bg-base-300">
              {renderActionButtons(isBusy)}

              <div class="px-3 py-2 flex flex-row gap-2 text-xs gap-5">
                <div>
                  <button
                    class="btn btn-sm  flex items-center gap-1"
                    onClick={(e) => {
                      e.preventDefault();
                      props.showChangeTableModal.value = true;
                    }}
                  >
                    <span class="material-symbols-outlined ">autorenew</span>
                    <span class="text-xs hidden">
                      {t("tables.general.change_table")}
                    </span>
                  </button>
                </div>
                <div>
                  <button
                    class="btn btn-sm  flex items-center gap-1"
                    onClick={(e) => {
                      e.preventDefault();
                      props.showTransferItemsModal.value = true;
                    }}
                  >
                    <span class="material-symbols-outlined">move_item</span>
                    <span class="text-xs hidden">Transfer Items</span>
                  </button>
                </div>
                <div>
                  <button
                    class="btn btn-sm  flex items-center gap-1"
                    disabled={isPrinting.value}
                    onClick={async () => {
                      try {
                        isPrinting.value = true;
                        await props.printReceipt();
                      } catch (e) {
                        console.error("Failed to print bill:", e);
                      } finally {
                        isPrinting.value = false;
                      }
                    }}
                  >
                    <span class="material-symbols-outlined">print</span>
                    <span class="text-xs hidden">
                      {t("tables.general.print_receipt")}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </BottomSheet>
      );
    };
  },
});
